import React, { useEffect, useState } from "react";
import grapesjs from "grapesjs";
import "grapesjs/dist/css/grapes.min.css";
import ThemeSettingsPanel from "./themeSettingPanel";

export default function SurveyBuilder({ user }) {
  const [theme, setTheme] = useState({
    color: "blue", // red | blue | green
    borderRadius: "md", // none | md | full
  });

  const handleSaveSurvey = async () => {
    const editor = window.surveyEditor;
    if (!editor) return;

    const html = editor.getHtml();
    const css = editor.getCss();

    const payload = {
      html,
      css,
      userId: user?._id || "anonymous",
      title: "Untitled Survey",
    };

    try {
      const res = await fetch("https://api.mb-techs.com/api/surveys", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (data.questaId) {
        alert(`Your survey link: https://questa.mb-techs.com/${data.questaId}`);
      } else {
        alert("Failed to save survey.");
      }
    } catch (err) {
      console.error(err);
      alert("An error occurred while saving the survey.");
    }
  };

  useEffect(() => {
    const editor = grapesjs.init({
      container: "#gjs",
      appendTo: "#styles",
      height: "100vh",
      width: "auto",
      storageManager: false,
      blockManager: { appendTo: "#blocks" },
      panels: { defaults: [] },
      ...(user?.plan === "pro" && {
        styleManager: {
          sectors: [
            {
              name: "Text",
              open: true,
              buildProps: ["text-align", "font-size", "font-weight", "color"],
              properties: [
                {
                  property: "text-align",
                  type: "radio",
                  defaults: "left",
                  list: [
                    { value: "left", name: "Left" },
                    { value: "center", name: "Center" },
                    { value: "right", name: "Right" },
                  ],
                },
              ],
            },
          ],
        },
      }),
    });

    window.surveyEditor = editor;

    const colorMap = {
      red: "#e3342f",
      blue: "#3490dc",
      green: "#38c172",
    };

    const radiusMap = {
      none: "0px",
      md: "6px",
      full: "9999px",
    };

    const injectStyles = () => {
      const color = colorMap[theme.color];
      const radius = radiusMap[theme.borderRadius];

      editor.setStyle(`
        label, .survey-container label {
          color: ${color};
          font-weight: bold;
        }

        input[type="text"],
        textarea,
        input[type="radio"],
        input[type="checkbox"] {
          border: 2px solid ${color};
          border-radius: ${radius};
          padding: 6px;
          margin: 4px 0;
        }

        input[type="radio"],
        input[type="checkbox"] {
          transform: scale(1.2);
          cursor: pointer;
        }

        .survey-container {
          width: 100%;
          max-width: 700px;
          margin: 0 auto;
          padding: 1rem;
          box-sizing: border-box;
        }

        .table-wrapper {
          overflow-x: auto;
          width: 100%;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          text-align: center;
        }

        @media (max-width: 600px) {
          table thead {
            display: none;
          }
          table, table tbody, table tr, table td {
            display: block;
            width: 100%;
          }
          table tr {
            margin-bottom: 1rem;
            border: 1px solid #ccc;
          }
          table td {
            text-align: left;
            padding: 0.5rem;
          }
        }
      `);
    };

    injectStyles();

    // Blocks
    editor.BlockManager.add("short_text", {
      label: "Short Text",
      content: `
        <div class="survey-container">
          <label contenteditable="true">Your question</label><br />
          <input type="text" placeholder="Short answer" style="width:100%;" />
        </div>
      `,
      category: "Basic",
    });

    editor.BlockManager.add("Title", {
      label: "Survey Title",
      content: `
        <div class="survey-container">
          <h2 contenteditable="true">Your Survey Title</h2>
        </div>
      `,
      category: "Basic",
    });

    if (user?.plan === "free") {
      editor.BlockManager.add("yes_no", {
        label: "Yes / No",
        content: `
          <div class="survey-container">
            <label contenteditable="true">Do you agree?</label><br />
            <input type="radio" name="yesno" /> Yes
            <input type="radio" name="yesno" /> No
          </div>
        `,
        category: "Basic",
      });
    } else {
      editor.BlockManager.add("comment_box", {
        label: "Comment Box",
        content: `
          <div class="survey-container">
            <label contenteditable="true">Leave a comment</label><br />
            <textarea placeholder="Your comment" style="width:100%; height: 80px;"></textarea>
          </div>
        `,
        category: "Basic",
      });

      editor.BlockManager.add("radio_group", {
        label: "Radio Group",
        content: `
          <div class="survey-container">
            <label contenteditable="true">Choose one:</label><br />
            <input type="radio" name="group1" /> Option A<br />
            <input type="radio" name="group1" /> Option B
          </div>
        `,
        category: "Advanced",
      });

      editor.BlockManager.add("checkbox_group", {
        label: "Checkbox Group",
        content: `
          <div class="survey-container">
            <label contenteditable="true">Select options:</label><br />
            <input type="checkbox" /> Option 1<br />
            <input type="checkbox" /> Option 2
          </div>
        `,
        category: "Advanced",
      });
    }

    // Satisfaction matrix + rows
    editor.DomComponents.addType("satisfaction-matrix", {
      model: {
        defaults: {
          tagName: "div",
          droppable: false,
          stylable: true,
          components: `
            <div class="survey-container">
              <div class="table-wrapper">
                <table id="matrix-table" border="1">
                  <thead>
                    <tr>
                      <th><div contenteditable="true">Question</div></th>
                      <th><div contenteditable="true">Very Satisfied</div></th>
                      <th><div contenteditable="true">Satisfied</div></th>
                      <th><div contenteditable="true">Neither</div></th>
                      <th><div contenteditable="true">Dissatisfied</div></th>
                      <th><div contenteditable="true">Very Dissatisfied</div></th>
                    </tr>
                  </thead>
                  <tbody data-gjs-droppable="tr" style="min-height: 40px;">
                    <tr>
                      <td><div contenteditable="true">Staff is helpful</div></td>
                      <td><input type="radio" name="q1" /></td>
                      <td><input type="radio" name="q1" /></td>
                      <td><input type="radio" name="q1" /></td>
                      <td><input type="radio" name="q1" /></td>
                      <td><input type="radio" name="q1" /></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          `,
        },
      },
    });

    editor.BlockManager.add("satisfaction_matrix", {
      label: "Satisfaction Matrix",
      content: { type: "satisfaction-matrix" },
      category: "Advanced",
    });

    editor.DomComponents.addType("satisfaction-row", {
      isComponent: (el) =>
        el.tagName === "TR" && el.classList?.contains("satisfaction-row"),
      model: {
        defaults: {
          tagName: "tr",
          classes: ["satisfaction-row"],
          draggable: true,
          droppable: false,
          stylable: false,
          components: [
            {
              tagName: "td",
              style: { textAlign: "left" },
              components: [
                {
                  tagName: "div",
                  content: "New Question",
                  attributes: { contenteditable: true },
                  stylable: true,
                },
              ],
            },
            ...Array.from({ length: 5 }).map(() => ({
              tagName: "td",
              components: [
                {
                  tagName: "input",
                  attributes: {
                    type: "radio",
                    name: `q${Date.now()}`,
                  },
                },
              ],
            })),
          ],
        },
        init() {
          const parent = this.parent();
          const root = editor.getWrapper();
          if (parent === root) {
            setTimeout(() => {
              const matrix = root.find("#matrix-table")[0];
              const tbody = matrix?.find("tbody")[0];
              if (tbody) {
                tbody.append(this);
              }
            }, 50);
          }
        },
      },
    });

    editor.BlockManager.add("satisfaction_row", {
      label: "Satisfaction Row",
      content: { type: "satisfaction-row" },
      category: "Advanced",
    });

    return () => editor.destroy();
  }, [theme, user?.plan]);

  return (
    <div className="flex h-screen">
      <div className="w-1/4 bg-gray-100 p-4 border-r overflow-y-auto flex flex-col">
        <ThemeSettingsPanel theme={theme} setTheme={setTheme} />
        <div id="blocks" className="mt-4 flex-1 overflow-y-auto" />
        <div id="styles" className="mt-4" />
        <button
          onClick={handleSaveSurvey}
          className="bg-blue-600 text-white p-2 rounded mt-4 hover:bg-blue-700"
        >
          Save Survey
        </button>
      </div>
      <div id="gjs" className="flex-1 overflow-y-auto" />
    </div>
  );
}
